import { useEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { memo } from "react";
import {
  Location,
  NavigationType,
  unstable_useBlocker,
} from "react-router-dom";
import { RedoModal } from "./arbiter-components/modal/redo-modal";

/**
 * Navigation state for a safe action that saved before leaving a dirty form:
 * `navigate("../", { state: skipUnsavedChangesGuardState })`.
 */
export const skipUnsavedChangesGuardState = { skipUnsavedChangesGuard: true };

/**
 *  Guards navigation away from a dirty form.
 *
 * - Blocks in-app route changes and tab close while `isDirty` is true.
 * - Exposes a `requestClose` handler that opens a confirm modal when dirty,
 *   or calls `onClose` directly when clean.
 * - `confirmClose` discards changes and bypasses the route guard so the user
 *   isn't prompted twice.
 */
export function useUnsavedChangesGuard({
  isDirty,
  blockQueryChanges = true,
  allowNavigation,
}: {
  isDirty: boolean;
  /** Set false when tab/panel/selection state lives in query params. */
  blockQueryChanges?: boolean;
  /** Opt a specific navigation out of the guard. */
  allowNavigation?: (args: {
    currentLocation: Location;
    nextLocation: Location;
  }) => boolean;
}) {
  const blocker = unstable_useBlocker(
    useHandler(
      ({
        currentLocation,
        nextLocation,
        historyAction,
      }: {
        currentLocation: Location;
        nextLocation: Location;
        historyAction: NavigationType;
      }) => {
        if (!isDirty) {
          return false;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const nextState = nextLocation.state as
          | typeof skipUnsavedChangesGuardState
          | null;
        // Ignore the flag on back/forward; it belongs to the original save.
        if (
          historyAction !== NavigationType.Pop &&
          nextState?.skipUnsavedChangesGuard
        ) {
          return false;
        }
        if (
          !blockQueryChanges &&
          currentLocation.pathname === nextLocation.pathname &&
          currentLocation.hash === nextLocation.hash
        ) {
          return false;
        }
        return !allowNavigation?.({ currentLocation, nextLocation });
      },
    ),
  );

  useEventListener(isDirty ? window : null, "beforeunload", (e) => {
    e.preventDefault();
    // https://bugs.chromium.org/p/chromium/issues/detail?id=866818
    e.returnValue = true;
  });

  /** Always resolve a blocked navigation; a stuck blocker swallows later ones. */
  return {
    confirmOpen: blocker.state === "blocked",
    discardChanges: useHandler(() => blocker.proceed?.()),
    keepEditing: useHandler(() => blocker.reset?.()),
  };
}

export const UnsavedChangesGuard = memo(function UnsavedChangesGuard(
  props: Parameters<typeof useUnsavedChangesGuard>[0],
) {
  const { confirmOpen, discardChanges, keepEditing } =
    useUnsavedChangesGuard(props);
  return confirmOpen ? (
    <UnsavedChangesModal
      onDiscard={discardChanges}
      onKeepEditing={keepEditing}
    />
  ) : null;
});

export const UnsavedChangesModal = memo(function UnsavedChangesModal({
  onDiscard,
  onKeepEditing,
}: {
  onDiscard: () => void;
  onKeepEditing: () => void;
}) {
  return (
    <RedoModal
      modalSize="sm"
      onModalCloseRequested={onKeepEditing}
      primaryButton={{
        text: "Discard changes",
        onClick: onDiscard,
        theme: "destructive",
        size: "sm",
      }}
      secondaryButton={{
        text: "Keep editing",
        onClick: onKeepEditing,
        size: "sm",
      }}
      subtitle="You have unsaved changes. If you leave now, they will be lost."
      theme="warn"
      title="Discard unsaved changes?"
    />
  );
});
